<script>
import { computed, reactive, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import {
  required,
  email,
  maxLength,
  alphaNum,
  helpers,
  requiredIf,
} from '@vuelidate/validators'
import axios from 'axios'
import Input from './components/Input.vue'
import Select from './components/Select.vue'
import InputErrors from './components/InputErrors.vue'
import Notification from './components/Notification.vue'

// since the required sign needs to be escaped in Input.vue and Select.vue, paste it here, so it does not get purged by tailwind compiler
// after:content-['*']

export default {
  props: ['params'],
  components: {
    Input,
    Select,
    InputErrors,
    Notification,
  },
  setup() {
    const loading = ref(false)
    const notifications = ref([])
    const index = ref(0)

    const requiredGerman = helpers.withMessage(
      'Dies ist ein Pflichtfeld.',
      required
    )
    const emailGerman = helpers.withMessage(
      'Bitte geben Sie eine gültige E-Mail-Adresse an.',
      email
    )
    const maxLengthFramenumberGerman = helpers.withMessage(
      'Die Eingabe darf nicht länger als 20 Zeichen sein.',
      maxLength(20)
    )
    const alphaNumGerman = helpers.withMessage(
      'Die Eingabe muss alpha-nummerisch sein.',
      alphaNum
    )

    const state = reactive({
      firstname: '',
      lastname: '',
      address: {
        street: '',
        postcode: '',
        city: '',
        country: null,
      },
      email: '',
      phone: '',

      topic: '',
      receipt: '',
      framenumber: '',

      subject: '',
      message: '',
      attachments: [],
    })

    const files = ref(null)
    const dropzone = ref({
      fileOverDropzone: false,
    })

    const rules = computed(() => {
      return {
        firstname: { requiredGerman },
        lastname: { requiredGerman },
        address: {
          street: { requiredGerman },
          postcode: { requiredGerman },
          city: { requiredGerman },
          country: { requiredGerman },
        },
        email: { requiredGerman, emailGerman },

        topic: { requiredGerman },
        framenumber: {
          maxLengthFramenumberGerman,
          alphaNumGerman,
          required: helpers.withMessage(
            'Dies ist ein Pflichtfeld.',
            requiredIf(() => state.topic === 'SERVICE')
          ),
        },

        subject: { requiredGerman },
        message: { requiredGerman },
      }
    })

    const v$ = useVuelidate(rules, state)

    return { index, loading, notifications, state, v$, files, dropzone }
  },
  methods: {
    async onFormSubmit() {
      this.notifications = []

      const isFormValid = await this.v$.$validate()
      if (!isFormValid) {
        document
          .querySelector('.has-error')
          .scrollIntoView({ behavior: 'smooth' })
        return
      }

      // reset validation "watch mode"
      this.$nextTick(() => {
        this.v$.$reset()
      })

      const baseUrl =
        process.env.NODE_ENV === 'production'
          ? 'https://ewms.hnf-nicolai.com'
          : 'http://localhost:5000'

      this.loading = true

      const formData = new FormData()
      for (let key in this.state) {
        if (key === 'address') {
          for (let aKey in this.state[key]) {
            if (this.state[key][aKey]) {
              formData.append(`address[${aKey}]`, this.state[key][aKey])
            }
          }
        } else if (key === 'attachments') {
          for (let i = 0; i < this.state.attachments.length; i++) {
            formData.append('attachments', this.state.attachments[i])
          }
        } else {
          if (this.state[key]) {
            formData.append(key, this.state[key])
          }
        }
      }

      try {
        const res = await axios.post(`${baseUrl}/customer-service`, formData)

        if (res.status === 201) {
          this.notifications.push({
            type: 'success',
            message: 'Ihre Daten wurden erfolgreich übermittelt.',
          })

          this.resetState()

          document
            .getElementById(this.$props.params?.containerId)
            .scrollIntoView({ behavior: 'smooth' })
        } else {
          this.notifications.push({
            type: 'warning',
            message:
              'Leider kam es zu einem Fehler in der Übertragung. Bitte versuchen Sie es erneut.',
          })
        }
      } catch (err) {
        console.log(err)
        this.notifications.push({
          type: 'error',
          message:
            'Leider kam es zu einem Fehler in der Übertragung. Bitte versuchen Sie es erneut.',
        })
      } finally {
        this.loading = false
      }
    },
    onFileChange(ev) {
      if (this.loading) {
        return
      }

      const files =
        ev.target.files && ev.target.files.length ? ev.target.files : null
      this.state.attachments = [].concat(this.state.attachments, [...files])
    },
    removeAttachment(index) {
      this.state.attachments.splice(index, 1)
    },
    removeNotification(index) {
      this.notifications.splice(index, 1)
    },
    resetState() {
      for (const key in this.state) {
        if (key === 'files') {
          this.state[key] = null
          continue
        }
        this.state[key] = ''
      }
      this.index++
    },
    dropFile(e) {
      let droppedFiles = e.dataTransfer.files
      if (!droppedFiles) return

      this.state.attachments = [].concat(this.state.attachments, [
        ...droppedFiles,
      ])
      this.dropzone.fileOverDropzone = false
    },
  },
}
</script>

<template>
  <div class="container mx-auto">
    <h3 v-if="params.title">{{ params.title }}</h3>
    <form
      ref="formElement"
      autocomplete="off"
      role="form"
      enctype="multipart/form-data"
      @submit.prevent="onFormSubmit"
      :key="index"
    >
      <div v-if="notifications && notifications.length" class="space-y-2 mb-4">
        <Notification
          v-for="(item, index) in notifications"
          :key="index"
          :type="item.type"
          @dismiss="removeNotification(index)"
          >{{ item.message }}</Notification
        >
      </div>

      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-y-2 mb-4">
        <Select
          id="topic"
          label="Thema"
          :options="{
            SERVICE: 'Unterstützung zum Bike',
            BACKOFFICE: 'Fragen zur Bestellung & Neubestellung',
            OTHER: 'Sonstiges',
          }"
          class="md:col-span-2 lg:col-span-4 text-gray-700"
          :loading="loading"
          :errors="v$.topic.$error"
          v-model="state.topic"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.topic.$errors"></InputErrors>
          </template>
        </Select>
        <Input
          id="receipt"
          label="Belegnummer"
          class="lg:col-span-2"
          :loading="loading"
          :errors="v$.receipt?.$error"
          v-model="state.receipt"
        >
          <template #errors>
            <InputErrors :errors="v$.receipt?.$errors"></InputErrors>
          </template>
        </Input>
        <Input
          id="framenumber"
          label="Rahmennummer"
          class="lg:col-span-2"
          :loading="loading"
          :errors="v$.framenumber?.$error"
          v-model="state.framenumber"
          :required="state.topic === 'SERVICE'"
        >
          <template #errors>
            <InputErrors :errors="v$.framenumber?.$errors"></InputErrors>
          </template>
        </Input>

        <Input
          id="subject"
          label="Betreff"
          class="lg:col-span-2"
          :loading="loading"
          :errors="v$.subject.$error"
          v-model="state.subject"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.subject.$errors"></InputErrors>
          </template>
        </Input>
        <Input
          id="message"
          label="Nachricht"
          class="md:col-span-2 lg:col-span-4"
          :class="{ 'lg:-mb-4': !v$.message.$error }"
          :loading="loading"
          :errors="v$.message.$error"
          type="textarea"
          max-characters="900"
          v-model="state.message"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.message.$errors"></InputErrors>
          </template>
        </Input>

        <div class="md:col-span-2 lg:col-span-4">
          <label class="block text-sm font-medium text-gray-700">
            Anhang
          </label>
          <div
            v-if="state.attachments && state.attachments.length"
            class="my-4"
          >
            <ul class="space-y-1">
              <li
                v-for="(file, index) in state.attachments"
                :key="index"
                class="flex items-center space-x-2 text-gray-700 cursor-pointer"
                @click="removeAttachment(index)"
              >
                <span>{{ file.name }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </li>
            </ul>
          </div>
          <div
            class="group mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md cursor-pointer"
            :class="[
              v$.attachments?.$error
                ? 'border-red-300'
                : dropzone.fileOverDropzone
                ? 'border-gray-600'
                : 'border-gray-300',
            ]"
            @dragover.prevent
            @dragenter.prevent="dropzone.fileOverDropzone = true"
            @dragleave.prevent="dropzone.fileOverDropzone = false"
            @drop.prevent="dropFile"
            @click="files.click()"
          >
            <div class="space-y-1 text-center">
              <div class="space-y-1 text-center">
                <svg
                  class="mx-auto h-12 w-12 text-gray-500 group-hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  ></path>
                </svg>
                <div class="flex justify-center text-sm">
                  <label
                    for="receipt"
                    class="relative cursor-pointer bg-white rounded-md font-medium text-gray-500 group-hover:text-gray-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-gray-500"
                  >
                    <span v-if="!state.receipt"
                      >Anhänge auswählen oder drag and drop</span
                    >
                    <input
                      id="attachments"
                      ref="files"
                      type="file"
                      @change="onFileChange"
                      class="sr-only"
                      accept="image/png, image/jpeg, .pdf"
                      multiple
                    />
                  </label>
                </div>
              </div>
              <p class="text-xs !text-gray-500">PNG, JPG, PDF bis zu 10MB</p>
            </div>
          </div>
          <InputErrors :errors="v$.attachments?.$errors"></InputErrors>
        </div>
      </div>

      <h3 class="text-base font-medium text-gray-600 mb-4">
        Ihre Kontaktdaten:
      </h3>

      <div
        class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-y-2 xl:grid-cols-6 mb-6"
      >
        <Input
          id="firstname"
          label="Vorname"
          class="lg:col-span-2 xl:col-span-3"
          :loading="loading"
          :errors="v$.firstname.$error"
          v-model="state.firstname"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.firstname.$errors"></InputErrors>
          </template>
        </Input>
        <Input
          id="lastname"
          label="Nachname"
          class="lg:col-span-2 xl:col-span-3"
          :loading="loading"
          :errors="v$.lastname.$error"
          v-model="state.lastname"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.lastname.$errors"></InputErrors>
          </template>
        </Input>

        <Input
          id="street"
          label="Straße"
          class="lg:col-span-4 xl:col-span-6"
          :loading="loading"
          :errors="v$.address.street.$error"
          v-model="state.address.street"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.address.street.$errors"></InputErrors>
          </template>
        </Input>
        <Input
          id="postcode"
          label="Postleitzahl"
          class="lg:col-span-2"
          :loading="loading"
          :errors="v$.address.postcode.$error"
          v-model="state.address.postcode"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.address.postcode.$errors"></InputErrors>
          </template>
        </Input>
        <Input
          id="city"
          label="Ort"
          class="lg:col-span-2"
          :loading="loading"
          :errors="v$.address.city.$error"
          v-model="state.address.city"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.address.city.$errors"></InputErrors>
          </template>
        </Input>
        <Select
          id="country"
          label="Land"
          :options="{
            de: 'Deutschland',
            at: 'Österreich',
            ch: 'Schweiz',
          }"
          class="md:col-span-2 lg:col-span-4 xl:col-span-2"
          :loading="loading"
          :errors="v$.address.country.$error"
          v-model="state.address.country"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.address.country.$errors"></InputErrors>
          </template>
        </Select>

        <Input
          id="email"
          label="Email"
          class="lg:col-span-2 xl:col-span-3"
          :loading="loading"
          :errors="v$.email.$error"
          v-model="state.email"
          required="true"
        >
          <template #errors>
            <InputErrors :errors="v$.email.$errors"></InputErrors>
          </template>
        </Input>
        <Input
          id="phone"
          label="Telefon"
          class="lg:col-span-2 xl:col-span-3"
          :loading="loading"
          :errors="v$.phone?.$error"
          v-model="state.phone"
        >
          <template #errors>
            <InputErrors :errors="v$.phone?.$errors"></InputErrors>
          </template>
        </Input>
      </div>

      <div class="mt-4">
        <button
          type="button"
          class="inline-flex items-center justify-center px-6 py-3 w-full md:w-1/2 lg:w-1/3 border border-transparent text-xs uppercase leading-6 font-medium rounded-md text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:border-gray-900 focus:shadow-outline-gray active:bg-gray-900 transition ease-in-out duration-150"
          :disabled="loading"
          @click="onFormSubmit"
        >
          <span v-if="!loading">Absenden</span>
          <span v-else>Absenden</span>
        </button>
      </div>
    </form>
  </div>
</template>
